import React from "react";
import { Badge, Button, Callout, Card, DataTable, Dialog, Field, Input, PageHeader, Select, Tabs } from "../components/DesignSystem";
import type { ID, TravelHouseholdSummary, TravelOverview } from "../domain/types";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";
import { ErrorState, LoadingState, useIdempotencyKey, useSubmitGuard } from "./common";

export function TravelScreen() {
  const { loading, error, service } = usePlannerData();
  const [tab, setTab] = React.useState<"households" | "hotels">("households");
  const [overview, setOverview] = React.useState<TravelOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = React.useState(false);
  const [overviewError, setOverviewError] = React.useState<string | null>(null);
  const [editingHousehold, setEditingHousehold] = React.useState<TravelHouseholdSummary | null>(null);
  const [addingArrivalFor, setAddingArrivalFor] = React.useState<TravelHouseholdSummary | null>(null);
  const [assigningRoomFor, setAssigningRoomFor] = React.useState<TravelHouseholdSummary | null>(null);
  const [addingBlock, setAddingBlock] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      setOverview(await service.travel.getOverview());
    } catch (err) {
      setOverviewError(errorMessage(err));
    } finally {
      setOverviewLoading(false);
    }
  }, [service]);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <div className="pob-pad"><LoadingState label="Loading travel & stay" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;

  const deleteArrival = async (arrivalId: ID) => {
    setActionError(null);
    try {
      await service.travel.deleteArrival(arrivalId);
      await load();
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };

  const deleteBlock = async (blockId: ID) => {
    setActionError(null);
    try {
      await service.travel.deleteHotelBlock(blockId);
      await load();
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Logistics"
        title="Travel & stay"
        meta={overview ? [
          { icon: "plane", label: `${overview.households.filter((h) => h.accommodationNeeded).length} households need accommodation` },
          // Product Excellence Pass 1 §3: mirrors Transport's "flagged, not yet requested" pattern
          // -- a household whose RSVP says they need accommodation but who has no room assignment
          // at all is a real gap, surfaced here rather than only discoverable by checking every row.
          ...(overview.households.some((h) => h.accommodationNeeded && h.roomAssignments.length === 0)
            ? [{ icon: "alert-triangle", label: `${overview.households.filter((h) => h.accommodationNeeded && h.roomAssignments.length === 0).length} flagged, no room assigned yet` }]
            : [])
        ] : []}
        actions={tab === "hotels" ? <Button variant="primary" icon="plus" onClick={() => setAddingBlock(true)}>Add hotel block</Button> : undefined}
        tabs={<Tabs value={tab} onChange={setTab} tabs={[{ value: "households", label: "Households" }, { value: "hotels", label: "Hotel blocks" }]} />}
      />
      <div className="pob-pad">
        {overviewLoading && !overview && <LoadingState label="Loading travel & stay" />}
        {overviewError && <ErrorState message={overviewError} retry={load} />}
        {actionError && <Callout tone="error" title="That didn't work" style={{ marginBottom: "var(--space-4)" }}>{actionError}</Callout>}

        {overview && tab === "households" && (
          <Card padding="0">
            <DataTable
              rows={overview.households}
              emptyLabel="No households yet"
              columns={[
                { key: "householdName", header: "Household" },
                { key: "accommodationNeeded", header: "Accommodation", width: 170, render: (row) => {
                  if (!row.accommodationNeeded) return <Badge tone="neutral">Not needed</Badge>;
                  return row.roomAssignments.length === 0 ? <Badge tone="warning">Needed — no room yet</Badge> : <Badge tone="accent">Needed</Badge>;
                } },
                { key: "travelOrigin", header: "Travelling from", muted: true, render: (row) => row.travelOrigin || "—" },
                { key: "rooms", header: "Rooms", width: 90, render: (row) => row.roomAssignments.reduce((sum, a) => sum + a.rooms, 0) || "—" },
                { key: "arrivals", header: "Arrivals", width: 90, render: (row) => row.arrivals.length || "—" },
                { key: "actions", header: "", width: 260, render: (row) => (
                  <span style={{ display: "flex", gap: "var(--space-2)" }}>
                    <Button size="sm" onClick={() => setEditingHousehold(row)}>Edit</Button>
                    <Button size="sm" onClick={() => setAddingArrivalFor(row)}>Add arrival</Button>
                    <Button size="sm" onClick={() => setAssigningRoomFor(row)}>Assign room</Button>
                  </span>
                ) }
              ]}
            />
          </Card>
        )}

        {overview && tab === "households" && overview.households.some((h) => h.arrivals.length > 0) && (
          <Card eyebrow="Arrivals" title="Flight / arrival details" padding="0" style={{ marginTop: "var(--space-5)" }}>
            <DataTable
              rows={overview.households.flatMap((h) => h.arrivals.map((a) => ({ ...a, householdName: h.householdName })))}
              columns={[
                { key: "householdName", header: "Household" },
                { key: "guestCount", header: "Guests", width: 80 },
                { key: "origin", header: "Origin", muted: true },
                { key: "arrivalDate", header: "Arrives", width: 110, muted: true },
                { key: "flightDetails", header: "Flight", muted: true },
                { key: "pickupNeeded", header: "Pickup", width: 90, render: (row) => row.pickupNeeded ? <Badge tone="accent">Needed</Badge> : "—" },
                { key: "departureDate", header: "Departs", width: 110, muted: true },
                { key: "actions", header: "", width: 90, render: (row) => <Button size="sm" variant="danger" onClick={() => deleteArrival(row.id)}>Remove</Button> }
              ]}
            />
          </Card>
        )}

        {overview && tab === "hotels" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
            {overview.hotelBlocks.length === 0 && <span style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>No hotel blocks yet.</span>}
            {overview.hotelBlocks.map((block) => (
              <Card key={block.id} eyebrow={block.code || "Room block"} title={block.hotelName} action={<Button size="sm" variant="danger" onClick={() => deleteBlock(block.id)}>Remove</Button>}>
                <div style={{ display: "flex", gap: "var(--space-2)", marginBottom: "var(--space-3)", flexWrap: "wrap" }}>
                  <Badge tone="neutral">{block.roomsHeld} rooms held</Badge>
                  {block.rate && <Badge tone="neutral">{block.rate}</Badge>}
                  {block.deadline && <Badge tone="neutral">Deadline {block.deadline}</Badge>}
                </div>
                <DataTable
                  rows={block.assignments}
                  emptyLabel="No households assigned yet"
                  columns={[
                    { key: "householdId", header: "Household", render: (row) => overview.households.find((h) => h.householdId === row.householdId)?.householdName || row.householdId },
                    { key: "rooms", header: "Rooms", width: 80 },
                    { key: "nights", header: "Nights", width: 80 },
                    { key: "paidBy", header: "Paid by", muted: true }
                  ]}
                />
              </Card>
            ))}
          </div>
        )}
      </div>

      {editingHousehold && (
        <EditHouseholdDialog
          household={editingHousehold}
          onClose={() => setEditingHousehold(null)}
          onSaved={async () => { setEditingHousehold(null); await load(); }}
        />
      )}
      {addingArrivalFor && (
        <ArrivalDialog
          household={addingArrivalFor}
          onClose={() => setAddingArrivalFor(null)}
          onSaved={async () => { setAddingArrivalFor(null); await load(); }}
        />
      )}
      {assigningRoomFor && overview && (
        <RoomAssignmentDialog
          household={assigningRoomFor}
          hotelBlocks={overview.hotelBlocks}
          onClose={() => setAssigningRoomFor(null)}
          onSaved={async () => { setAssigningRoomFor(null); await load(); }}
        />
      )}
      {addingBlock && (
        <HotelBlockDialog
          onClose={() => setAddingBlock(false)}
          onSaved={async () => { setAddingBlock(false); await load(); }}
        />
      )}
    </div>
  );
}

function EditHouseholdDialog({ household, onClose, onSaved }: { household: TravelHouseholdSummary; onClose: () => void; onSaved: () => Promise<void> }) {
  const { service } = usePlannerData();
  const [accommodationNeeded, setAccommodationNeeded] = React.useState(household.accommodationNeeded);
  const [travelOrigin, setTravelOrigin] = React.useState(household.travelOrigin || "");
  const [formError, setFormError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();

  const submit = () => guardedSubmit(async () => {
    setFormError(null);
    try {
      await service.travel.updateHouseholdLogistics(household.householdId, { accommodationNeeded, travelOrigin: travelOrigin.trim() || null });
      await onSaved();
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog
      title={`Travel details — ${household.householdName}`}
      description="RSVP accommodation answers set this automatically once submitted; adjust it here if a household hasn't RSVP'd yet."
      onClose={onClose}
      footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" onClick={submit} disabled={submitting}>{submitting ? "Saving…" : "Save"}</Button></>}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {formError && <Callout tone="error" title="Check the form">{formError}</Callout>}
        <Field label="Accommodation">
          <Select value={accommodationNeeded ? "yes" : "no"} onChange={(event) => setAccommodationNeeded(event.target.value === "yes")} options={[{ value: "yes", label: "Needed" }, { value: "no", label: "Not needed" }]} />
        </Field>
        <Field label="Travelling from"><Input value={travelOrigin} onChange={(event) => setTravelOrigin(event.target.value)} placeholder="e.g. Toronto, Canada" /></Field>
      </div>
    </Dialog>
  );
}

function ArrivalDialog({ household, onClose, onSaved }: { household: TravelHouseholdSummary; onClose: () => void; onSaved: () => Promise<void> }) {
  const { service } = usePlannerData();
  const [guestCount, setGuestCount] = React.useState("1");
  const [origin, setOrigin] = React.useState(household.travelOrigin || "");
  const [arrivalDate, setArrivalDate] = React.useState("");
  const [flightDetails, setFlightDetails] = React.useState("");
  const [pickupNeeded, setPickupNeeded] = React.useState(false);
  const [departureDate, setDepartureDate] = React.useState("");
  const [formError, setFormError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();
  const idempotencyKey = useIdempotencyKey();

  const submit = () => guardedSubmit(async () => {
    setFormError(null);
    const parsed = Number(guestCount);
    if (!Number.isFinite(parsed) || parsed < 1) { setFormError("Guest count must be at least 1."); return; }
    try {
      await service.travel.createArrival({ householdId: household.householdId, guestCount: parsed, origin: origin || undefined, arrivalDate: arrivalDate || undefined, flightDetails: flightDetails || undefined, pickupNeeded, departureDate: departureDate || undefined }, idempotencyKey);
      await onSaved();
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog
      title={`Add arrival — ${household.householdName}`}
      onClose={onClose}
      footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" onClick={submit} disabled={submitting}>{submitting ? "Adding…" : "Add arrival"}</Button></>}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {formError && <Callout tone="error" title="Check the form">{formError}</Callout>}
        <Field label="Guests travelling"><Input value={guestCount} type="number" onChange={(event) => setGuestCount(event.target.value)} /></Field>
        <Field label="Origin"><Input value={origin} onChange={(event) => setOrigin(event.target.value)} placeholder="London" /></Field>
        <Field label="Arrival date"><Input value={arrivalDate} type="date" onChange={(event) => setArrivalDate(event.target.value)} /></Field>
        <Field label="Flight details"><Input value={flightDetails} onChange={(event) => setFlightDetails(event.target.value)} placeholder="BA 123, 14:20" /></Field>
        <Field label="Departure date"><Input value={departureDate} type="date" onChange={(event) => setDepartureDate(event.target.value)} /></Field>
        <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", font: "var(--type-body-sm)" }}>
          <input type="checkbox" checked={pickupNeeded} onChange={(event) => setPickupNeeded(event.target.checked)} />
          Airport pickup needed
        </label>
      </div>
    </Dialog>
  );
}

function RoomAssignmentDialog({ household, hotelBlocks, onClose, onSaved }: { household: TravelHouseholdSummary; hotelBlocks: { id: ID; hotelName: string }[]; onClose: () => void; onSaved: () => Promise<void> }) {
  const { service } = usePlannerData();
  const [hotelRoomBlockId, setHotelRoomBlockId] = React.useState(hotelBlocks[0]?.id || "");
  const [rooms, setRooms] = React.useState("1");
  const [nights, setNights] = React.useState("1");
  const [paidBy, setPaidBy] = React.useState("");
  const [formError, setFormError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();
  const idempotencyKey = useIdempotencyKey();

  const submit = () => guardedSubmit(async () => {
    setFormError(null);
    if (!hotelRoomBlockId) { setFormError("Add a hotel block first."); return; }
    const parsedRooms = Number(rooms);
    const parsedNights = Number(nights);
    if (!Number.isFinite(parsedRooms) || parsedRooms < 1 || !Number.isFinite(parsedNights) || parsedNights < 1) { setFormError("Rooms and nights must be at least 1."); return; }
    try {
      await service.travel.createRoomAssignment({ householdId: household.householdId, hotelRoomBlockId, rooms: parsedRooms, nights: parsedNights, paidBy: paidBy || undefined }, idempotencyKey);
      await onSaved();
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog
      title={`Assign room — ${household.householdName}`}
      onClose={onClose}
      footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" onClick={submit} disabled={submitting}>{submitting ? "Assigning…" : "Assign room"}</Button></>}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {formError && <Callout tone="error" title="Check the form">{formError}</Callout>}
        <Field label="Hotel block"><Select value={hotelRoomBlockId} onChange={(event) => setHotelRoomBlockId(event.target.value)} options={hotelBlocks.map((b) => ({ value: b.id, label: b.hotelName }))} /></Field>
        <Field label="Rooms"><Input value={rooms} type="number" onChange={(event) => setRooms(event.target.value)} /></Field>
        <Field label="Nights"><Input value={nights} type="number" onChange={(event) => setNights(event.target.value)} /></Field>
        <Field label="Paid by (optional)"><Input value={paidBy} onChange={(event) => setPaidBy(event.target.value)} placeholder="Groom's family" /></Field>
      </div>
    </Dialog>
  );
}

function HotelBlockDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => Promise<void> }) {
  const { service } = usePlannerData();
  const [hotelName, setHotelName] = React.useState("");
  const [code, setCode] = React.useState("");
  const [rate, setRate] = React.useState("");
  const [roomsHeld, setRoomsHeld] = React.useState("10");
  const [formError, setFormError] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();
  const idempotencyKey = useIdempotencyKey();

  const submit = () => guardedSubmit(async () => {
    setFormError(null);
    const parsed = Number(roomsHeld);
    if (!hotelName.trim()) { setFormError("Give the hotel a name."); return; }
    if (!Number.isFinite(parsed) || parsed < 0) { setFormError("Rooms held must be 0 or more."); return; }
    try {
      await service.travel.createHotelBlock({ hotelName: hotelName.trim(), code: code || undefined, rate: rate || undefined, roomsHeld: parsed }, idempotencyKey);
      await onSaved();
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <Dialog
      title="Add hotel block"
      onClose={onClose}
      footer={<><Button size="sm" onClick={onClose} disabled={submitting}>Cancel</Button><Button size="sm" variant="primary" onClick={submit} disabled={submitting}>{submitting ? "Adding…" : "Add hotel block"}</Button></>}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {formError && <Callout tone="error" title="Check the form">{formError}</Callout>}
        <Field label="Hotel name"><Input value={hotelName} onChange={(event) => setHotelName(event.target.value)} placeholder="Serena Hotel" /></Field>
        <Field label="Booking code (optional)"><Input value={code} onChange={(event) => setCode(event.target.value)} /></Field>
        <Field label="Rate (optional)"><Input value={rate} onChange={(event) => setRate(event.target.value)} placeholder="PKR 24,000/night" /></Field>
        <Field label="Rooms held"><Input value={roomsHeld} type="number" onChange={(event) => setRoomsHeld(event.target.value)} /></Field>
      </div>
    </Dialog>
  );
}
